import React from 'react';
import ReactDOM from 'react-dom';
import AsyncSelect from 'react-select/async';
import C3Chart from 'react-c3js';
import 'c3/c3.css';

// Renders the SmartSUSTAIN "Lifecycle Analytics" tab: headline reuse metrics,
// a trend chart, category/project/building breakdowns, top reused styles and a
// filtered activity table. All figures come from /dashboard/lifecycle_analytics.
class LifecycleAnalytics extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      startDate: props.startDate || '',
      endDate: props.endDate || '',
      project: props.project || '',
      eventType: '',
      appliedEventType: '',
      appliedQuery: '',
      data: null,
      showTemplates: false,
      templatesOpen: false
    };
  }

  componentWillUnmount() {
    clearTimeout(this._modalTimer);
    document.body.style.overflow = '';
  }

  componentDidMount() {
    this.loadData();
  }

  queryParams() {
    const params = new URLSearchParams();
    if (this.state.startDate) params.append('start_date', this.state.startDate);
    if (this.state.endDate) params.append('end_date', this.state.endDate);
    if (this.state.project) params.append('project', this.state.project);
    if (this.state.eventType) params.append('event_type', this.state.eventType);
    return params.toString();
  }

  loadData = () => {
    const { eventType } = this.state;
    const query = this.queryParams();
    this.setState({ loading: true });
    fetch(`/dashboard/lifecycle_analytics?${query}`)
      .then((res) => res.json())
      .then((res) => {
        if (res.success) {
          this.setState({ data: res.analytics, loading: false, appliedEventType: eventType, appliedQuery: query });
        } else {
          this.setState({ data: null, loading: false });
        }
      })
      .catch(() => this.setState({ loading: false }));
  };

  loadProjects = (input) => (
    input ? fetch(`/dashboard/project_search?q=${input}`).then((res) => res.json()) : Promise.resolve([])
  );

  handleProject = (selected) => this.setState({ project: selected ? selected.value : '' });

  exportUrl() {
    return `/dashboard/lifecycle_analytics/export?${this.state.appliedQuery}`;
  }

  findMeUrl() {
    const codes = (this.state.data && this.state.data.asset_codes) || [];
    if (codes.length) {
      return `/items/search?em_assets_codes=${encodeURIComponent(codes.join(','))}`;
    }
    return '/items';
  }

  assetCodes() {
    return (this.state.data && this.state.data.asset_codes) || [];
  }

  csrfToken() {
    const el = document.querySelector('meta[name="csrf-token"]');
    return el ? el.getAttribute('content') : '';
  }

  openDataDumpTemplates = () => {
    if (!this.assetCodes().length) return;
    clearTimeout(this._modalTimer);
    document.body.style.overflow = 'hidden';
    this.setState({ showTemplates: true, templatesOpen: false }, () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.setState({ templatesOpen: true }));
      });
    });
  };

  closeDataDumpTemplates = () => {
    this.setState({ templatesOpen: false });
    clearTimeout(this._modalTimer);
    this._modalTimer = setTimeout(() => {
      this.setState({ showTemplates: false });
      document.body.style.overflow = '';
    }, 220);
  };

  render() {
    const { loading, data, startDate, endDate, project, eventType, showTemplates } = this.state;
    const { lifecycleEvents = [], canGenerateReport, reportTemplates = [] } = this.props;

    return (
      <div className="lc-analytics">
        {this.renderFilters(startDate, endDate, project, eventType, lifecycleEvents, canGenerateReport)}

        {loading && <div className="lc-loading">Loading lifecycle analytics…</div>}

        {!loading && !data && (
          <div className="lc-empty">Unable to load lifecycle analytics.</div>
        )}

        {!loading && data && data.total_activities === 0 && (
          <div className="lc-empty">No lifecycle events match the selected filters.</div>
        )}

        {!loading && data && data.total_activities > 0 && (
          <React.Fragment>
            {this.renderHeadline(data.headline)}
            {this.renderTrend(data.trend)}
            <div className="lc-grid-3">
              {this.renderBars('Reuse by category', data.by_category, '#2F8D37')}
              {this.renderBars('Top projects', data.by_project, '#3C93C9')}
              {this.renderBars('Top buildings', data.by_building, '#8484bf')}
            </div>
            {this.renderTopStyles(data.top_styles)}
            {this.renderActivities(data.activities, data.total_activities)}
          </React.Fragment>
        )}

        {showTemplates && this.renderTemplateModal(reportTemplates)}

        {this.styles()}
      </div>
    );
  }

  renderFilters(startDate, endDate, project, eventType, lifecycleEvents, canGenerateReport) {
    const selectStyles = {
      control: (base) => ({
        ...base,
        minHeight: '42px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '0.9rem',
        boxShadow: 'none'
      }),
      menu: (base) => ({ ...base, zIndex: 20 })
    };

    return (
      <div className="lc-card lc-filters">
        <div className="lc-filters-header">
          <h2 className="lc-filters-title">Filters</h2>
          <p className="lc-filters-desc">Select a date range, project, or lifecycle event to refine the analytics below.</p>
        </div>

        <div className="lc-filters-grid">
          <div className="lc-field">
            <label htmlFor="lc-start-date">From</label>
            <div className="lc-date-wrap">
              <input
                id="lc-start-date"
                type="date"
                value={startDate}
                onChange={(e) => this.setState({ startDate: e.target.value })}
              />
            </div>
          </div>

          <div className="lc-field">
            <label htmlFor="lc-end-date">To</label>
            <div className="lc-date-wrap">
              <input
                id="lc-end-date"
                type="date"
                value={endDate}
                onChange={(e) => this.setState({ endDate: e.target.value })}
              />
            </div>
          </div>

          <div className="lc-field lc-field-wide">
            <label>Project</label>
            <AsyncSelect
              cacheOptions
              defaultOptions
              isClearable
              placeholder="All projects"
              loadOptions={this.loadProjects}
              onChange={this.handleProject}
              value={project ? { value: project, label: project } : null}
              styles={selectStyles}
            />
          </div>

          <div className="lc-field lc-field-wide">
            <label htmlFor="lc-event-type">Lifecycle event</label>
            <select
              id="lc-event-type"
              value={eventType}
              onChange={(e) => this.setState({ eventType: e.target.value })}
            >
              <option value="">All Reuse Events</option>
              <option value="all">All lifecycle events</option>
              {lifecycleEvents.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="lc-filters-actions">
          <button type="button" className="lc-btn lc-btn-primary" onClick={this.loadData}>Apply filters</button>
          {/* <a className="lc-btn lc-btn-secondary" href={this.exportUrl()} target="_blank" rel="noopener noreferrer">Export CSV</a> */}
          <a className="lc-btn lc-btn-secondary" href={this.findMeUrl()}>Open in FindME</a>
          {canGenerateReport && (
            <button
              type="button"
              className="lc-btn lc-btn-secondary"
              onClick={this.openDataDumpTemplates}
              disabled={!this.assetCodes().length}
            >
              Generate Data Dump
            </button>
          )}
        </div>
      </div>
    );
  }

  renderTemplateModal(reportTemplates) {
    const codes = this.assetCodes().join(',');
    const token = this.csrfToken();
    const openClass = this.state.templatesOpen ? ' is-open' : '';

    return ReactDOM.createPortal(
      (
        <div className={`lc-modal-backdrop${openClass}`} onClick={this.closeDataDumpTemplates}>
          <div className="lc-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="lc-modal-header">
              <h3>Your Report Templates</h3>
              <button type="button" className="lc-modal-close" onClick={this.closeDataDumpTemplates} aria-label="Close">×</button>
            </div>
            <div className="lc-modal-body">
              {reportTemplates.length === 0 && (
                <p className="lc-muted">You haven&apos;t created any report templates yet</p>
              )}
              {reportTemplates.map((template) => (
                <form
                  key={template.id}
                  method="post"
                  action={`/reports/new?template_id=${template.id}`}
                  className="lc-template-row"
                >
                  <input type="hidden" name="authenticity_token" value={token} />
                  <input type="hidden" name="report_type" value="Data_Dump" />
                  <input type="hidden" name="filters[em_assets_codes]" value={codes} />
                  <span className="lc-template-type">{template.type}</span>
                  <span className="lc-template-name">{template.name}</span>
                  <button type="submit" className="btn -variation-reset -color-blue">Use</button>
                </form>
              ))}
            </div>
          </div>
        </div>
      ),
      document.body
    );
  }

  filterLabels() {
    const { appliedEventType } = this.state;
    const { lifecycleEvents = [] } = this.props;

    if (!appliedEventType) {
      return {
        assets: 'Assets reused',
        events: 'Reuse events',
        trendEvents: 'Reuse events',
        trendAssets: 'Assets reused',
        periodVerb: 'reused'
      };
    }

    if (appliedEventType === 'all') {
      return {
        assets: 'Assets',
        events: 'Lifecycle events',
        trendEvents: 'Lifecycle events',
        trendAssets: 'Assets',
        periodVerb: 'recorded'
      };
    }

    const name = (lifecycleEvents.find((e) => e.value === appliedEventType) || {}).label
      || appliedEventType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

    return {
      assets: `Assets ${name}`,
      events: `${name} events`,
      trendEvents: `${name} events`,
      trendAssets: `Assets ${name}`,
      periodVerb: name.toLowerCase()
    };
  }

  renderHeadline(h) {
    const labels = this.filterLabels();
    const cards = [
      {
        key: 'assets',
        label: labels.assets,
        value: h.assets_reused,
        sub: `${h.assets_reused_month} ${labels.periodVerb} this month · ${h.assets_reused_year} ${labels.periodVerb} this year`
      },
      { key: 'events', label: labels.events, value: h.reuse_events },
      { key: 'co2', label: 'CO₂e avoided', value: `${h.co2_avoided.toLocaleString()} kg` },
      { key: 'cost', label: 'Cost savings', value: `£${h.cost_savings.toLocaleString()}` },
      {
        key: 'category',
        label: 'Top category',
        value: h.top_category ? h.top_category.label : '—',
        sub: h.top_category ? `${h.top_category.count} events` : ''
      }
    ];

    return (
      <div className="lc-headline">
        {cards.map((c) => (
          <div className="lc-card lc-metric" key={c.key}>
            <div className="lc-metric-value">{c.value}</div>
            <div className="lc-metric-label">{c.label}</div>
            {c.sub && <div className="lc-metric-sub">{c.sub}</div>}
          </div>
        ))}
      </div>
    );
  }

  renderTrend(trend) {
    if (!trend.length) return null;

    const labels = this.filterLabels();
    const data = {
      x: 'x',
      columns: [
        ['x', ...trend.map((t) => t.period)],
        [labels.trendEvents, ...trend.map((t) => t.events)],
        [labels.trendAssets, ...trend.map((t) => t.assets)]
      ],
      type: 'line',
      colors: { [labels.trendEvents]: '#3C93C9', [labels.trendAssets]: '#2F8D37' }
    };

    return (
      <div className="lc-card lc-section">
        <h3 className="lc-section-title">{labels.trendEvents.replace(/ events$/, '')} activity over time</h3>
        <C3Chart
          data={data}
          axis={{
            x: { type: 'category' },
            y: {
              min: 0,
              padding: { bottom: 0 },
              tick: {
                format: (d) => (Number.isInteger(d) ? d : '')
              }
            }
          }}
          bar={{ width: { ratio: 0.5 } }}
        />
      </div>
    );
  }

  renderBars(title, rows, color) {
    const max = rows.reduce((m, r) => Math.max(m, r.count), 0) || 1;

    return (
      <div className="lc-card lc-section">
        <h3 className="lc-section-title">{title}</h3>
        {rows.length === 0 && <p className="lc-muted">No data</p>}
        {rows.map((row) => (
          <div className="lc-bar-row" key={row.label}>
            <span className="lc-bar-label" title={row.label}>{row.label}</span>
            <span className="lc-bar-track">
              <span className="lc-bar-fill" style={{ width: `${(row.count / max) * 100}%`, backgroundColor: color }} />
            </span>
            <span className="lc-bar-value">{row.count}</span>
          </div>
        ))}
      </div>
    );
  }

  renderTopStyles(styles) {
    if (!styles.length) return null;

    return (
      <div className="lc-card lc-section">
        <h3 className="lc-section-title">Top reused styles</h3>
        <div className="lc-styles">
          {styles.map((style) => (
            <div className="lc-style" key={style.catalogue_item_id}>
              <div className="lc-style-img">
                {style.image
                  ? <img src={style.image} alt={style.name} />
                  : <span className="lc-style-placeholder">No image</span>}
              </div>
              <div className="lc-style-name" title={style.name}>{style.name}</div>
              <div className="lc-style-meta">{style.count} reuses · {style.co2.toLocaleString()} kg</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderActivities(rows, total) {
    return (
      <div className="lc-card lc-section">
        <div className="lc-section-head">
          <h3 className="lc-section-title">Lifecycle activity</h3>
          <span className="lc-muted">Showing {rows.length} of {total} events</span>
        </div>
        <div className="lc-table-wrap">
          <table className="lc-table">
            <thead>
              <tr>
                <th>Date</th><th>Event</th><th>Asset</th><th>Category</th><th>Project</th><th>User</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td><span className={`lc-tag lc-tag-${row.group}`}>{row.event}</span></td>
                  <td><a href={`/items/${row.asset_id}`}>{row.asset_code || row.asset_id}</a></td>
                  <td>{row.category}</td>
                  <td>{row.project}</td>
                  <td>{row.user}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  styles() {
    return (
      <style>{`
        .lc-analytics { margin-top: 0; }
        .lc-card { background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.08); padding: 24px; margin-bottom: 24px; }
        .lc-loading, .lc-empty { text-align: center; padding: 48px; color: #637381; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.08); }

        .lc-filters { padding: 24px 24px 20px; }
        .lc-filters-header { margin-bottom: 20px; }
        .lc-filters-title { font-size: 1.35rem; font-weight: 700; color: #212B35; margin: 0 0 6px; }
        .lc-filters-desc { font-size: 0.9rem; color: #637381; margin: 0; line-height: 1.45; }

        .lc-filters-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
          align-items: end;
          margin-bottom: 20px;
        }
        .lc-field { display: flex; flex-direction: column; min-width: 0; }
        .lc-field-wide { grid-column: span 1; }
        .lc-field label {
          display: block;
          font-size: 0.9rem;
          font-weight: 500;
          color: #212B35;
          margin-bottom: 8px;
        }
        .lc-date-wrap { position: relative; }
        .lc-date-wrap input,
        .lc-field select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.9rem;
          height: 42px;
          background: #fff;
          color: #212B35;
          box-sizing: border-box;
        }
        .lc-field select { appearance: auto; cursor: pointer; }

        .lc-filters-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
          padding-top: 4px;
          border-top: 1px solid #eef1f5;
        }
        .lc-btn {
          padding: 10px 22px;
          border-radius: 6px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          border: none;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          line-height: 1.2;
          transition: background-color 0.15s;
        }
        .lc-btn-primary { background: #3C93C9; color: #fff; }
        .lc-btn-primary:hover { background: #2d7ba8; }
        .lc-btn-secondary { background: #eef1f5; color: #212B35; }
        .lc-btn-secondary:hover { background: #e2e7ee; }
        .lc-btn:disabled { opacity: 0.5; cursor: not-allowed; }

        .lc-modal-backdrop {
          position: fixed; inset: 0; z-index: 10050;
          background: rgba(33, 43, 53, 0.45);
          display: flex; align-items: center; justify-content: center;
          padding: 24px;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .lc-modal-backdrop.is-open { opacity: 1; }
        .lc-modal {
          background: #fff; border-radius: 8px; width: 100%; max-width: 520px;
          max-height: 80vh; overflow: auto; box-shadow: 0 8px 32px rgba(0,0,0,0.18);
          opacity: 0;
          transform: translateY(16px) scale(0.97);
          transition: opacity 0.22s ease, transform 0.22s ease;
        }
        .lc-modal-backdrop.is-open .lc-modal {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .lc-modal-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 20px; border-bottom: 1px solid #eef1f5;
        }
        .lc-modal-header h3 { margin: 0; font-size: 1.1rem; color: #212B35; }
        .lc-modal-close {
          border: none; background: transparent; font-size: 1.5rem;
          line-height: 1; cursor: pointer; color: #637381; padding: 0 4px;
        }
        .lc-modal-body { padding: 16px 20px 20px; }
        .lc-template-row {
          display: flex; align-items: center; gap: 12px;
          padding: 12px; margin-bottom: 10px;
          background: #f4f6f8; border-radius: 6px;
        }
        .lc-template-type { font-size: 0.8rem; color: #637381; white-space: nowrap; }
        .lc-template-name { flex: 1; font-weight: 600; color: #212B35; }

        .lc-headline { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; margin-bottom: 24px; }
        .lc-metric { text-align: center; margin-bottom: 0; }
        .lc-metric-value { font-size: 1.8rem; font-weight: 700; color: #212B35; }
        .lc-metric-label { font-size: 0.85rem; color: #637381; margin-top: 4px; }
        .lc-metric-sub { font-size: 0.72rem; color: #9aa5b1; margin-top: 4px; }

        .lc-section-title { font-size: 1.05rem; font-weight: 600; color: #212B35; margin: 0 0 16px; }
        .lc-section-head { display: flex; justify-content: space-between; align-items: baseline; }
        .lc-grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
        .lc-muted { color: #9aa5b1; font-size: 0.85rem; }

        .lc-bar-row { display: grid; grid-template-columns: 110px 1fr 36px; align-items: center; gap: 10px; margin-bottom: 10px; }
        .lc-bar-label { font-size: 0.82rem; color: #44515f; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .lc-bar-track { background: #eef1f5; border-radius: 999px; height: 10px; overflow: hidden; }
        .lc-bar-fill { display: block; height: 100%; border-radius: 999px; }
        .lc-bar-value { font-size: 0.82rem; font-weight: 600; text-align: right; color: #212B35; }

        .lc-styles { display: grid; grid-template-columns: repeat(5, 1fr); gap: 16px; }
        .lc-style { text-align: center; }
        .lc-style-img { height: 171px; border-radius: 8px; background: #f4f6f8; display: flex; align-items: center; justify-content: center; overflow: hidden; margin-bottom: 8px; }
        .lc-style-img img { max-width: 100%; max-height: 100%; object-fit: contain;width: auto; height: auto;}
        .lc-style-placeholder { color: #b0b8c1; font-size: 0.8rem; }
        .lc-style-name { font-size: 0.85rem; font-weight: 600; color: #212B35; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .lc-style-meta { font-size: 0.75rem; color: #637381; margin-top: 2px; }

        .lc-table-wrap { overflow-x: auto; }
        .lc-table { width: 100%; border-collapse: collapse; font-size: 0.85rem; }
        .lc-table th { text-align: left; padding: 10px 12px; color: #637381; font-weight: 600; border-bottom: 2px solid #eef1f5; white-space: nowrap; }
        .lc-table td { padding: 10px 12px; border-bottom: 1px solid #f0f2f5; color: #44515f; }
        .lc-table a { color: #3C93C9; text-decoration: none; }
        .lc-tag { display: inline-block; padding: 2px 8px; border-radius: 999px; font-size: 0.72rem; font-weight: 600; }
        .lc-tag-life_cycle_events { background: #e8f2fc; color: #1565c0; }
        .lc-tag-reuse_events { background: #e8f5e9; color: #2e7d32; }

        @media (max-width: 1100px) {
          .lc-headline, .lc-styles { grid-template-columns: repeat(2, 1fr); }
          .lc-grid-3 { grid-template-columns: 1fr; }
          .lc-filters-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 600px) {
          .lc-filters-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    );
  }
}

export default LifecycleAnalytics;
